import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Grid,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  TableContainer,
  IconButton,
  Tooltip,
  AutocompleteInputChangeReason,
  Button,
  Modal,
  Divider,
} from "@mui/material";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { Controller, useForm } from "react-hook-form";
import ArrowDropUpTwoToneIcon from '@mui/icons-material/ArrowDropUpTwoTone';
import ArrowDropDownTwoToneIcon from '@mui/icons-material/ArrowDropDownTwoTone';
import { debounce } from "lodash";

import fontWeight from "../../../styles/mui/fontWeight";
import {
  TableMain,
  TableMainHead,
} from "../../AuthorizationDetails/Style/DisciplineStyle";
import {
  defaultFormValues,
  GetRolesActionDispatchTypes,
  HeaderLabel,
  USER_STATUS_DROPDOWN_OPTION,
} from "../../../constants/User";
import CustomTablePagination from "../../../App/components/CustomTablePagination";
import { PaginationPayload } from "../../../models/Authorization";
import {
  AutoComplete,
  boxStyle,
  CancelButton,
  CancelProviderModalGrid,
  CenterTypography,
  ClearServiceModalSubtitle,
  ContentBox,
  FilterParameterGrid,
  FooterBox,
  HeaderLabelStyle,
  IconBox,
  ModalTitle,
  SearchTitleGrid,
} from "../styles/style";
import { displayFlex } from "../../../styles/mui/styles/display";
import { FormInputDropdown } from "../../../components/formComponents/FormInputDropdown";
import { getList, getValue } from "../../../utils";
import {
  HealthPlanMaster,
  User,
  UserRole,
  UserSearchPayload,
  UserStatusPayload,
} from "../../../models/User";
import VirtualizeAutocomplete from "../../../components/formComponents/AutocompleteList";
import { IconFaMdClose, ModalGridContainer } from "../../../styles/common/style";
import { colors } from "../../../styles/colors";
import { ASC, DESC } from "../../../constants/FunctionalUtils";
import CentralLoader from "../../../App/components/CentralLoader";
import { resetStates } from "../../../actions/Common";


export interface PropsFromDispatch {
  getUsers: (payload: UserSearchPayload) => void;
  getRoles: () => void;
  getHealthPlanMasterResp: () => void;
  updateUserStatus:(payload: UserStatusPayload)=>void;
}

export interface PropsFromState {
  userData: {
    response: {
      totalRows: number;
      searchResponse: User[];
    } | null;
    loading: boolean;
  };
  roles: UserRole[];
  healthPlanMaster: HealthPlanMaster[];
  updateUserStatusResponse: boolean;
  updateUserStatusLoading: boolean;
}

type AllProps = PropsFromDispatch & PropsFromState;

const UserManagement: React.FC<AllProps> = ({
  userData,
  getUsers,
  roles,
  getRoles,
  healthPlanMaster,
  getHealthPlanMasterResp,
  updateUserStatusResponse,
  updateUserStatusLoading,
  updateUserStatus,
}) => {

  const { response, loading } = userData;
  const totalRows = response?.totalRows ?? 0;
  const users = response?.searchResponse ?? ([] as User[]);
  const [visibleUsers, setVisibleUsers] = useState<User[]>(users);
  const [deactivatedUserIds, setDeactivatedUserIds] = useState<string[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [pendingDeactivateUserId, setPendingDeactivateUserId] = useState<string | null>(null);
  const [openConfirmDeactivateUserModal, setOpenConfirmDeactivateUserModal] = useState(false);
  const [paginationData, setPaginationData] = useState<UserSearchPayload>(
    defaultFormValues,
  );
  const [valueToOrderBy, setValueToOrderBy] = useState<string>("UserName");
  const [orderDirection, setOrderDirection] = useState(ASC);
  const [openProvider, setOpenProvider] = useState(false);
  const prevUpdateUserStatusLoading = useRef<boolean>(false);

  const methods = useForm<any>({
    defaultValues: defaultFormValues,
    mode: "all",
  });

  const { control, getValues, setValue, watch } = methods;
  const fieldValue= watch();

const debouncedGetUsers = useRef(
  debounce((payload: UserSearchPayload) => {
    setVisibleUsers([])
    resetStates([GetRolesActionDispatchTypes.GET_ROLES_RESET]);
    getUsers(payload);
  }, 500)
).current;

useEffect(() => {
  debouncedGetUsers(paginationData);
  return () => debouncedGetUsers.cancel();
}, [paginationData, debouncedGetUsers]);

  const normalizeDropdownValue = (value: unknown) =>
    value === "" ? null : value;

  const handlePagination = (pageData: PaginationPayload) => {
    const payload: UserSearchPayload = {
      ...paginationData,
      searchKeyword:getValue(fieldValue, "searchKeyword", ""),
      roleId: normalizeDropdownValue(getValue(fieldValue, "roleId", null)) as string | null,
      healthPlanId: normalizeDropdownValue(getValue(fieldValue, "healthPlanId", null)) as number | null,
      status: normalizeDropdownValue(getValue(fieldValue, "status", null)) as boolean | null,
      sortColumn: valueToOrderBy || "userName",
      sortOrder: orderDirection,
      pageNo: pageData.pageNo,
      pageSize: pageData.pageSize,
    };
    setPaginationData(payload);
  };

  const handleSort = (field?: string) => {
    if (!field) return;
    const isASC = valueToOrderBy === field && orderDirection === ASC;
    const newDirection = isASC ? DESC : ASC;
    setOrderDirection(newDirection);
    setValueToOrderBy(field);
    const payload: UserSearchPayload = {
      ...paginationData,
      searchKeyword:getValue(fieldValue, "searchKeyword", ""),
      roleId: normalizeDropdownValue(getValue(fieldValue, "roleId", null)) as string | null,
      healthPlanId: normalizeDropdownValue(getValue(fieldValue, "healthPlanId", null)) as number | null,
      status: normalizeDropdownValue(getValue(fieldValue, "status", null)) as boolean | null,
      pageNo: paginationData.pageNo,
      pageSize: paginationData.pageSize,
      sortColumn: field,
      sortOrder: newDirection,
    };
    setValueToOrderBy(field);
    setOrderDirection(newDirection);
    setPaginationData(payload);
  };


  useEffect(() => {
    setVisibleUsers(users);
  }, [users]);

  useEffect(() => {
    if (
      prevUpdateUserStatusLoading.current &&
      !updateUserStatusLoading &&
      pendingDeactivateUserId
    ) {
      if (updateUserStatusResponse) {
        setDeactivatedUserIds((prevIds) =>
          prevIds.includes(pendingDeactivateUserId)
            ? prevIds
            : [...prevIds, pendingDeactivateUserId]
        );
      }
      setPendingDeactivateUserId(null);
    }
    prevUpdateUserStatusLoading.current = updateUserStatusLoading;
  }, [updateUserStatusLoading, updateUserStatusResponse, pendingDeactivateUserId]);

  const handleDeactivateUser = (userId: string) => {
    setSelectedUserId(userId);
    setOpenConfirmDeactivateUserModal(true);
  };

  const confirmDeactivateUser = () => {
    if (selectedUserId !== null) {
      setPendingDeactivateUserId(selectedUserId);
      updateUserStatus({ userId: selectedUserId, isStatusActive: false });
    }

    setSelectedUserId(null);
    setOpenConfirmDeactivateUserModal(false);
  };

  const userRolesList = getList(roles, {
    label: "roleName",
    value: "roleId",
  });

  const sortedHealthPlans = [...(healthPlanMaster || [])].sort((a, b) =>
    String(a.healthPlanName).toLowerCase().localeCompare(String(b.healthPlanName).toLowerCase())
  );

  const healthPlansList = getList(sortedHealthPlans, {
    label: "healthPlanName",
    value: "healthPlanId",
  });

  useEffect(() => {
    getRoles();
    getHealthPlanMasterResp();
  }, []);
  

  const handleProviderName = (
    _event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason !== "input") return;

    const rawValue = value;               // keep exactly what user typed
    const queryValue = value.trim();      // use only for API/search logic

    // keep user text unchanged in UI
    setValue("searchKeyword", rawValue);

    // reset filters for free-text search
    setValue("roleId", null);
    setValue("healthPlanId", null);
    setValue("status", null);

    const payload: UserSearchPayload = {
      searchKeyword: queryValue,
      roleId: null,
      healthPlanId: null,
      status: null,
      sortColumn: valueToOrderBy || "UserName",
      sortOrder: orderDirection,
      pageNo: 1,
      pageSize: paginationData.pageSize,
    };

    // trigger API only when actual search text is 3+ chars, or fully cleared
    if (queryValue.length >= 3 || rawValue.length === 0) {
      setPaginationData(payload);
    }
  };

   const debouncedSearch = useRef(
     debounce((event, value, reason) => {
       handleProviderName(event, value, reason);
     }, 500)
   ).current;
   
   useEffect(() => {
     return () => debouncedSearch.cancel();
   }, [debouncedSearch]);

  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    debouncedSearch(event, value, reason);
  };

  const handleDropdownChange = (event: any) => {
    const fieldName = event?.target?.name;
    const fieldValue = normalizeDropdownValue(event?.target?.value);

    if (!fieldName) {
      return;
    }

    // When applying any filter, clear the free-text search field and reset to page 1
    const updatedValues = {
      ...getValues(),
      [fieldName]: fieldValue,
      searchKeyword: "",
    };

    setValue(fieldName, fieldValue);
    setValue("searchKeyword", "");

    const payload: UserSearchPayload = {
      searchKeyword: "",
      roleId: normalizeDropdownValue(getValue(updatedValues, "roleId", null)) as string | null,
      healthPlanId: normalizeDropdownValue(getValue(updatedValues, "healthPlanId", null)) as number | null,
      status: normalizeDropdownValue(getValue(updatedValues, "status", null)) as boolean | null,
      sortColumn: valueToOrderBy || "userName",
      sortOrder: orderDirection,
      pageNo: 1,
      pageSize: paginationData.pageSize,
    };

    setPaginationData(payload);
  };

  return (
    <Grid>
      <Typography
        variant="h1"
        fontWeight={fontWeight.Weight[5]}
        mt={"2rem"}
        ml={"2rem"}
      >
        USER MANAGEMENT
      </Typography>
      <Grid size={12}>
        <Box sx={boxStyle}>
          {loading ? (
            <CentralLoader/>
          ) : (
            <>
              <Grid size={12} mt={"1rem"} pb={"1rem"} sx={displayFlex}>
                <Grid container size={"grow"}>
                  <Grid size={3.5} sx={CenterTypography}>
                    <Typography variant="h3" fontWeight={fontWeight.Weight[5]} >
                      USERS ({users.length})
                    </Typography>
                  </Grid>
                  <Grid size={2.24} sx={SearchTitleGrid}>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        color={colors.fonts[2]}
                        fontWeight={fontWeight.Weight[3]}
                        pb={"0rem"}
                      >
                        Search:
                      </Typography>
                      <Controller
                        name={"searchKeyword"}
                        defaultValue={""}
                        control={control}
                        render={({ field }) => {
                          return (
                            <>
                              <VirtualizeAutocomplete
                                options={[]}
                                value={field.value ?? ""}
                                handleInputChange={(event, value, reason) => {
                                  field.onChange(value);
                                  handleInputChange(event, value, reason);
                                }}
                                sx={AutoComplete}
                                open={openProvider}
                                setOpen={setOpenProvider}
                                maxLength={128}
                                width="55rem"
                                loading={loading}
                                placeholder="Search by name, role, health plan"
                              />
                            </>
                          );
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid size={1.5} sx={CenterTypography}>
                    <Typography
                      color={colors.black[1]}
                      fontWeight={fontWeight.Weight[6]}
                      variant="subtitle2"
                    >
                      OR
                    </Typography>
                    <Typography
                      color={colors.black[1]}
                      fontWeight={fontWeight.Weight[6]}
                      variant="subtitle2"
                    >
                      FILTER BY:
                    </Typography>
                  </Grid>
                  <Grid size={1.42} sx={FilterParameterGrid}>
                    <FormInputDropdown
                      name="roleId"
                      control={control}
                      label="Role:"
                      list={userRolesList}
                      onChangeHandler={handleDropdownChange}
                    />
                  </Grid>
                  <Grid size={1.42} sx={FilterParameterGrid}>
                    <FormInputDropdown
                      name="healthPlanId"
                      control={control}
                      label="Health Plan:"
                      list={healthPlansList}
                      onChangeHandler={handleDropdownChange}
                    />
                  </Grid>
                  <Grid size={1.42} sx={FilterParameterGrid}>
                    <FormInputDropdown
                      name="status"
                      control={control}
                      label="Status:"
                      list={USER_STATUS_DROPDOWN_OPTION}
                      onChangeHandler={handleDropdownChange}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <TableContainer>
                <TableMain aria-label="simple table">
                  <TableMainHead
                    sx={{
                      "& th:first-of-type": {
                        borderRadius: 0,
                      },
                      "& th:last-child": {
                        borderRadius: 0,
                      },
                    }}
                  >
                    <TableRow sx={ HeaderLabelStyle }>
                      {HeaderLabel.map((header, index) => (
                        <TableCell
                          key={index}
                          align="left"
                          sx={HeaderLabelStyle}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {header.label}
                            {header.field && (
                              <IconButton
                                size="small"
                                onClick={() => handleSort(header.field)}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  p: 0.25,
                                  gap: "-2px",
                                }}
                              >
                                <ArrowDropUpTwoToneIcon
                                  fontSize="small"
                                  sx={{    
                                    color:
                                      valueToOrderBy === header.field &&
                                      orderDirection === ASC
                                        ? colors.primary.main
                                        : colors.fonts[2],
                                      marginBottom: "-10px",  
                                  }}
                                />
                                <ArrowDropDownTwoToneIcon
                                  fontSize="small"
                                  sx={{
                                    
                                    color:
                                      valueToOrderBy === header.field &&
                                      orderDirection === DESC
                                        ? colors.primary.main
                                        : colors.fonts[2],
                                        
        
                                  }}
                                />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableMainHead>
                  <TableBody>
                    {visibleUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No users found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleUsers.map((user) => (
                        <TableRow
                          key={user.userId}
                          sx={{
                             opacity:
                               deactivatedUserIds.includes(user.userId) || !user.isStatusActive
                                 ? 0.6
                                 : 1,
                          }}
                        >
                          <TableCell>{user.userName}</TableCell>
                          <TableCell>{user.emailId}</TableCell>
                          <TableCell>{user.role}</TableCell>
                          <TableCell>{user.healthPlan}</TableCell>
                          <TableCell>{deactivatedUserIds.includes(user.userId) || !user.isStatusActive
                               ? "Inactive"
                               : "Active"}
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                "Deactivate User"
                              }
                            >
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeactivateUser(user.userId)}
                                  disabled={
                                     deactivatedUserIds.includes(user.userId) || !user.isStatusActive
                                   }
                                >
                                  <PersonRemoveIcon fontSize="small" sx={{ color: colors.primary.main }}/>
                                </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </TableMain>
              </TableContainer>

              <CustomTablePagination
                totalRecords={totalRows}
                paginationData={paginationData}
                handlePagination={handlePagination}
                paginationInjectedTo="user-management"
              />
            </>
          )}
        </Box>
      </Grid>
      <ConfirmDeactivateUserModal
        openConfirmDeactivateUserModal={openConfirmDeactivateUserModal}
        closeConfirmDeactivateUserModal={setOpenConfirmDeactivateUserModal}
        onConfirmDeactivateUser={confirmDeactivateUser}
      />
    </Grid>
  );
};
type ConfirmDeactivateUserModalProps = {
  openConfirmDeactivateUserModal: boolean;
  closeConfirmDeactivateUserModal: (onClose: boolean) => void;
  onConfirmDeactivateUser: () => void;
};

const ConfirmDeactivateUserModal = ({
  openConfirmDeactivateUserModal,
  closeConfirmDeactivateUserModal,
  onConfirmDeactivateUser,
}: ConfirmDeactivateUserModalProps) => {

  const handleClose = () => {
    closeConfirmDeactivateUserModal(false);
  };
  const handleConfirmDeactivateUser = () => {
    onConfirmDeactivateUser();
  };
  
  return (
    <>
      <Modal
        open={openConfirmDeactivateUserModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={ModalGridContainer}
      >
        <Box sx={CancelProviderModalGrid}>
          <Grid container sx={ContentBox}>
            <Grid size={11.2}>
              <Typography
                variant="h6"
                color={colors.fonts[20]}
                fontWeight={fontWeight.Weight[5]}
                sx={ModalTitle}
              >
                Deactivate User
              </Typography>
            </Grid>
            <Grid size={0.5} sx={IconBox}>
              <IconFaMdClose
                onClick={handleClose}
                data-testid="close-deactivate-user"
              />
            </Grid>
            <Grid size={12}>
              <Divider/>
            </Grid>
            <Box mt={"1rem"}>
              <Box>
                <Typography
                  variant="subtitle1"
                  color={colors.black[2]}
                  fontWeight={fontWeight.Weight[4]}
                  sx={ClearServiceModalSubtitle}
                >
                  Are you sure you want to deactivate this user?
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Box sx={FooterBox}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={CancelButton}
              data-testid="popup-cancel-deactivate-user"
            >
              CANCEL
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDeactivateUser}
              data-testid="popup-deactivate-user"
            >
              Deactivate
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
export default UserManagement;
